import { useEffect, useState } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import PausePolicyInfoMonthView from '@/dashboard/components/Products/policyCard/components/infoView/monthInfo';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { GridItem } from '@/dashboard/mui/styledComponent';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { XCircleIcon } from '@heroicons/react/outline';
import {
  PolicyPremiumDetailInfoResponse,
  apiGetCancelPauseDetailInfoByCancelDate,
  apiGetPausePolicyPremiumDetailInfo,
  apiRequestCancelPolicyWithFileInfo,
  convertDayjsTimeToServerStoredTimeData,
} from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

type CancelPolicyDialogConfirmProps = CustomDialogProps & {
  policy: PolicyModel;
  submitInfo: {
    cancelDate: Dayjs;
    reason: string;
  };
  back: () => void;
};

const CancelPolicyDialogConfirm = (props: CancelPolicyDialogConfirmProps) => {
  const {
    policy,
    submitInfo: { cancelDate, reason },
    back,
    close,
    success,
  } = props;
  const [loading, setLoading] = useState(false);
  const [{ fetchPolicies }] = usePolicyStore(({ actions }) => [actions]);
  const [cardSelectMonth, setCardSelectMonth] = useState('');
  const [premiumDetailInfo, setPremiumDetailInfo] = useState<PolicyPremiumDetailInfoResponse>({ billing_info: [] });

  /**
   * request
   */
  // get request params
  const getRequestParams = () => {
    const params = {
      cancellation_date: convertDayjsTimeToServerStoredTimeData(cancelDate),
      policy_id: policy?.id,
    };
    if (reason) params['reason'] = reason;

    return params;
  };
  const requestCancelPauseDetailInfo = async () => {
    const params = getRequestParams() as any;
    params.hideErrToast = true;

    setLoading(true);
    const [res] = await apiGetCancelPauseDetailInfoByCancelDate(params);
    setLoading(false);

    if (res) {
      if (res.billing_info.length > 0) {
        setCardSelectMonth(res.billing_info[0].monthText);
      }
      setPremiumDetailInfo(res);
    }
  };
  // extend pause
  const requestCancelPolicy = async () => {
    const params = getRequestParams();

    setLoading(true);
    const [res] = await apiRequestCancelPolicyWithFileInfo(params);
    setLoading(false);

    if (res) {
      fetchPolicies();
      if (success) success();
    }
  };

  useEffect(() => {
    requestCancelPauseDetailInfo();
  }, []);

  /**
   * methods
   */
  const closeAction = () => {
    if (close) close();
  };

  const backAction = () => {
    if (back) back();
  };

  // click card
  const clickCardAction = (index) => {
    setCardSelectMonth(premiumDetailInfo.billing_info[index].monthText);
  };

  return (
    <Box className="relative mt-4 flex flex-col items-center">
      {loading && <LogoLoading />}
      <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
        <XCircleIcon className="h-7 w-7" />
      </Box>
      <Typography className="text-black-secondary mt-4 text-center font-black" variant="h5">
        Cancel insurance
      </Typography>
      <Box className="flex flex-row items-center">
        <Typography className="text-black-secondary text-center" variant="body2">
          Cancelation date : {dayjs(cancelDate).format('DD.MMM.YYYY')}
        </Typography>
        <Box className="flex flex-row items-center">
          <span className="bg-brand-primary ml-2 h-[6px] w-[6px] rounded-full border"></span>
          <Button variant="text" onClick={backAction}>
            Change dates
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2} className="mt-2">
        {premiumDetailInfo.billing_info.map((item, index) => {
          return (
            <Grid item key={item.monthText} xs={12 / premiumDetailInfo.billing_info.length}>
              <GridItem>
                <PausePolicyInfoMonthView index={index} info={item} click={clickCardAction} isActive={cardSelectMonth === item.monthText} />
              </GridItem>
            </Grid>
          );
        })}
      </Grid>
      <Box className="flex flex-row items-center">
        <Typography variant="subtitle2">You will recieve a refund to your original payment method.</Typography>
        {/* <Button variant="text">You can update it here.</Button> */}
      </Box>
      <Box className="mt-6 flex w-full flex-row justify-end">
        <Button className="text-brand-primary text-lg font-bold" variant="text" onClick={closeAction}>
          Stay protected
        </Button>
        <LoadingButton
          className="ml-2 h-12 w-[190px] rounded-full text-lg font-bold"
          loading={loading}
          loadingIndicator="Loading..."
          color="primary"
          onClick={requestCancelPolicy}
          variant="contained"
        >
          <span>Cancel insurance</span>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default CancelPolicyDialogConfirm;
